// El cuestionario de onboarding es obligatorio hasta terminarlo — también al NAVEGAR.
// Ver ADR-2026-09-23-el-cuestionario-es-obligatorio-tambien-al-navegar.
//
// El rebote de `App` (`checkUserProfile`) corre sólo en eventos de sesión: al arrancar
// y en `SIGNED_IN`. Una navegación SPA no pasa por ahí. O sea que a quien pagó por el
// alta por link y tiene la empresa con el rubro centinela, cualquier salida de la cadena
// post-pago que cayera en la app —el "Volver" del portal de facturación tras refrescar
// el detalle de una factura, por ejemplo— lo dejaba adentro sin cuestionario hasta la
// próxima recarga. La obligación valía al entrar, no al moverse.
//
// Dos piezas, las dos sin estado en la base:
//   · un flag a nivel de módulo, que `checkUserProfile` prende cuando LEE el centinela
//     (lectura positiva, igual que el rebote: nunca por un error) y que el cuestionario
//     apaga cuando escribe el rubro. Sobrevive los remontes del árbol de rutas porque no
//     es un ref; muere con la pestaña, que es lo correcto: la próxima carga vuelve a leer.
//   · un guardia montado junto al router que, en cada cambio de ruta, manda al
//     cuestionario si el flag está prendido y la ruta no está exenta.
//
// La lista de rutas exentas es UNA sola y la usan los dos (el rebote y el guardia):
// tenerla dos veces es exactamente cómo se desincronizan.

import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import { rutaDelOnboarding, type OnboardingDebido } from './onboardingDebido';
import { olvidarPagoConMarcaPendiente } from './pagoConMarcaPendiente';

/**
 * Rutas donde NO se interrumpe a quien tiene el cuestionario pendiente. Prefijos.
 *
 *  · el cuestionario mismo (si no, loop),
 *  · el alta por link y todas las pantallas donde se paga o se acaba de pagar:
 *    interrumpir a alguien camino a pagar, o entre el pago y la oferta de factura, es
 *    peor que esperar a que salga de ahí,
 *  · los flujos que crean la empresa por su cuenta (Tiendanube) o que no tienen que
 *    crearla (invitación a un equipo),
 *  · las pantallas de auth: sin sesión no hay cuestionario que deber.
 */
export const RUTAS_EXENTAS_DEL_CUESTIONARIO: readonly string[] = [
  ONBOARDING_V2.setup, // '/configuracion-cuenta' y sus pasos (el cuestionario corto)
  ONBOARDING_V2.marcaSetup, // '/configurar-marca' y sus pasos (el onboarding de marca)
  '/alta-empresa',
  '/capacitacion-ultra',
  '/facturacion',
  '/checkout',
  '/subscription',
  '/verificar-celular',
  '/tiendanube',
  '/unirme',
  '/auth',
  '/registro',
  '/restablecer-clave',
];

/** ¿En esta ruta se deja en paz a quien tiene el cuestionario pendiente? Pura. */
export function rutaExentaDelCuestionario(ruta: string | null | undefined): boolean {
  const r = ruta ?? '';
  return RUTAS_EXENTAS_DEL_CUESTIONARIO.some((prefijo) => r.startsWith(prefijo));
}

// Qué onboarding se debe, no sólo "si" (ADR-2026-10-31-onboarding-corto-para-la-prueba): desde
// que hay dos, el guardia tiene que saber a cuál mandar, y apagar uno no puede apagar el otro.
// El onboarding de marca escribe el rubro real en el tablero, ANTES de terminar: si eso apagara
// todo, quien acaba de pagar se escaparía en el canal o en las piezas.
let pendiente: OnboardingDebido = null;

/**
 * Cuántas veces se apagó el flag. Existe por una carrera que dejaba prendido el flag de
 * más (ADR-2026-10-20-el-onboarding-no-rebota-con-una-lectura-vieja).
 *
 * Supabase dispara `SIGNED_IN` cada vez que la pestaña vuelve a primer plano, y con eso
 * `checkUserProfile` vuelve a leer el rubro. En Android, dentro de Instagram o Facebook,
 * la galería saca la página a segundo plano: al volver de elegir el logo arranca una
 * lectura que todavía ve el centinela. Si la persona confirma la marca mientras esa
 * lectura viaja, el cuestionario apaga el flag y DESPUÉS llega la lectura vieja y lo vuelve
 * a prender. Al tocar "Entrar a Atlas", el guardia la mandaba de vuelta al onboarding.
 *
 * Quien lee anota la versión ANTES de la consulta (`versionDelCuestionario`) y, al volver,
 * sólo prende el flag si nadie lo apagó en el medio. Apagar no se condiciona: un rubro
 * real leído es siempre verdad.
 */
let version = 0;

/** La versión del flag, para anotarla antes de una lectura y compararla al volver. */
export function versionDelCuestionario(): number {
  return version;
}

/**
 * ¿Esta lectura salió antes de que alguien apagara el flag? Pura sobre la versión anotada.
 * Si es vieja, su "falta el cuestionario" ya no vale: no prende nada ni manda a nadie.
 */
export function lecturaVieja(versionAlLeer: number): boolean {
  return versionAlLeer !== version;
}

/**
 * Lo llama `checkUserProfile` cuando LEYÓ que a la empresa le falta un onboarding, y el pago
 * cuando la persona acaba de pagar y debe el de marca. SÓLO con lectura positiva: un error de
 * red no puede prender esto, por la misma razón por la que el rebote falla cerrado.
 *
 * La marca manda sobre el cuestionario: si ya se debe la marca, una lectura que sólo ve el
 * centinela no la baja.
 */
export function marcarOnboardingPendiente(debido: Exclude<OnboardingDebido, null>): void {
  if (pendiente === 'marca') return;
  pendiente = debido;
}

/**
 * Lo llama el cuestionario apenas escribe el rubro real (el corto, y el tablero del onboarding
 * de marca). Si no lo apagara, la pantalla de resultados —que navega a la app— sería rebotada
 * de vuelta al paso 1: el loop que hay que evitar. Apaga SÓLO el cuestionario: la marca la apaga
 * `onboardingDeMarcaCompletado`.
 */
export function cuestionarioCompletado(): void {
  if (pendiente === 'cuestionario') pendiente = null;
  // La versión se mueve SIEMPRE, aunque el flag no estuviera prendido: una lectura que salió
  // antes de que se escribiera el rubro ya es vieja (ADR-2026-10-20-el-onboarding-no-rebota-con-una-lectura-vieja).
  version += 1;
}

/** Lo llama el final del onboarding de marca, una vez que la base dice que ya no se debe. */
export function onboardingDeMarcaCompletado(): void {
  pendiente = null;
  version += 1;
  olvidarPagoConMarcaPendiente();
}

/** Cerrar sesión, o que la sesión cambie de dueño: el flag es de módulo, no de usuario. */
export function olvidarOnboardingPendiente(): void {
  pendiente = null;
  version += 1;
}

/** Lo que se debe según la memoria de esta pestaña. */
export function onboardingPendienteEnMemoria(): OnboardingDebido {
  return pendiente;
}

/** Adónde hay que mandar a quien está en esta ruta, o `null` si a ningún lado. Pura sobre el flag + la ruta. */
export function adondeDebeIr(ruta: string | null | undefined): string | null {
  if (!pendiente || rutaExentaDelCuestionario(ruta)) return null;
  return rutaDelOnboarding(pendiente);
}

/** ¿Hay que mandar a esta ruta a algún onboarding? */
export function debeIrAlCuestionario(ruta: string | null | undefined): boolean {
  return adondeDebeIr(ruta) !== null;
}
